import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { useSelector } from 'react-redux';
import axiosInstance from '../utils/axiosInstance';
import { isAmxUser } from '../utils/auth';
import MenuButton from './MenuButton';

const POLL_MS = 60000;

function relativeTime(value) {
  if (!value) {
    return '';
  }
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return '';
  }
  const seconds = Math.max(1, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) {
    return 'Just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationsMenu() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const open = Boolean(anchorEl);
  const notificationsEnabled = isAmxUser(user);

  const fetchNotifications = React.useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
    }
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.get('/notifications', { params: { limit: 20 } });
      setNotifications(Array.isArray(response.data?.notifications) ? response.data.notifications : []);
      setUnreadCount(Number(response.data?.unread_count || 0));
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        console.error('Failed to load notifications', error);
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, [notificationsEnabled]);

  React.useEffect(() => {
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }
    fetchNotifications({ quiet: true });
    const timer = window.setInterval(() => fetchNotifications({ quiet: true }), POLL_MS);
    return () => window.clearInterval(timer);
  }, [fetchNotifications, notificationsEnabled]);

  if (!notificationsEnabled) {
    return null;
  }

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markRead = async (notification) => {
    if (notification.read_at) {
      return notification;
    }
    const response = await axiosInstance.patch(`/notifications/${notification.notification_id}`, { read: true });
    const updated = response.data?.notification || { ...notification, read_at: new Date().toISOString() };
    setNotifications((items) => items.map((item) => (
      item.notification_id === notification.notification_id ? updated : item
    )));
    setUnreadCount((count) => Math.max(0, count - 1));
    return updated;
  };

  const handleSelect = async (notification) => {
    try {
      const updated = await markRead(notification);
      handleClose();
      const targetUrl = updated.target_url || notification.target_url;
      if (targetUrl) {
        if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
          window.location.assign(targetUrl);
        } else {
          navigate(targetUrl);
        }
      }
    } catch (error) {
      console.error('Failed to open notification', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axiosInstance.post('/notifications/read-all');
      const readAt = new Date().toISOString();
      setNotifications((items) => items.map((item) => ({ ...item, read_at: item.read_at || readAt })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications read', error);
    }
  };

  return (
    <React.Fragment>
      <MenuButton
        showBadge={unreadCount > 0}
        aria-label="Open notifications"
        aria-controls={open ? 'notifications-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
      >
        <NotificationsRoundedIcon />
      </MenuButton>
      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 320, sm: 380 },
              maxWidth: 'calc(100vw - 24px)',
              mt: 1,
            },
          },
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          <Button
            size="small"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
            sx={{ minWidth: 0 }}
          >
            Mark read
          </Button>
        </Stack>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet.
            </Typography>
          </Box>
        ) : notifications.map((notification) => (
          <MenuItem
            key={notification.notification_id}
            onClick={() => handleSelect(notification)}
            sx={{
              alignItems: 'flex-start',
              whiteSpace: 'normal',
              gap: 1.25,
              py: 1.25,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: notification.read_at ? 'transparent' : 'error.main',
                flex: '0 0 auto',
                mt: 0.75,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: notification.read_at ? 500 : 700 }}>
                {notification.title}
              </Typography>
              {notification.body ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {notification.body}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {relativeTime(notification.created_at)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
}
